import { countries, countryMatchesSearch } from "@redotech/locale/countries";
import { useTranslation } from "@redotech/redo-i18n";
import { memo, type ReactNode, useMemo, useState } from "react";
import type { TextRedoListItem } from "./arbiter-components/list/redo-list";
import {
  type RedoDropdownInputSize,
  type RedoDropdownInputState,
  RedoSingleSelectDropdownInput,
} from "./arbiter-components/select-dropdown/redo-single-select-dropdown-input";

function regionDisplayNames(language: string): Intl.DisplayNames | undefined {
  try {
    return new Intl.DisplayNames([language], { type: "region" });
  } catch {
    return undefined;
  }
}

/** Searchable single-country dropdown with names in the UI language. */
export const CountrySelectDropdownInput = memo(
  function CountrySelectDropdownInput({
    countryCode,
    setCountryCode,
    description,
    disabled,
    fitToAnchor,
    fullWidth,
    label,
    placeholder,
    size,
    state,
  }: {
    countryCode: string | undefined;
    setCountryCode(countryCode: string): void;
    description?: string;
    disabled?: boolean;
    fitToAnchor?: boolean;
    fullWidth?: boolean;
    label?: string | ReactNode;
    placeholder?: string;
    size?: RedoDropdownInputSize;
    state?: RedoDropdownInputState;
  }) {
    const { t, i18n } = useTranslation();
    const [searchString, setSearchString] = useState("");

    const allOptions = useMemo<TextRedoListItem<string>[]>(() => {
      const displayNames = regionDisplayNames(i18n.language);
      return countries
        .map(({ code, name }) => ({
          type: "text" as const,
          id: code,
          value: code,
          text: displayNames?.of(code) ?? name,
        }))
        .sort((a, b) => a.text.localeCompare(b.text, i18n.language));
    }, [i18n.language]);

    const options = useMemo(
      () =>
        allOptions.filter((option) =>
          countryMatchesSearch(option.value, searchString, option.text),
        ),
      [allOptions, searchString],
    );

    const selectedItem = useMemo(
      () => allOptions.find((option) => option.value === countryCode),
      [allOptions, countryCode],
    );

    return (
      <RedoSingleSelectDropdownInput
        description={description}
        disabled={disabled}
        fitToAnchor={fitToAnchor}
        fullWidth={fullWidth}
        label={label}
        options={options}
        optionSelected={({ item }) => setCountryCode(item.value)}
        placeholder={placeholder}
        searchPlaceholder={t("Search countries...")}
        searchString={searchString}
        selectedItem={selectedItem}
        setSearchString={setSearchString}
        size={size}
        state={state}
      />
    );
  },
);
